import type { HourlySlot } from './energyHistory'
import { runBatterySimulation, type BatteryStrategy } from './batterySimulation'
import { resolveTariff, spotTariff, type TariffSource } from './tariffs'

/** Percentile of hourly net grid power used for inverter sizing (avoids one-off outliers). */
export const INVERTER_SIZING_PERCENTILE = 95
/** Common retail home-battery pack sizes we compare in the advice. */
export const STANDARD_BATTERY_CAPACITIES_KWH = [2.5, 5, 10, 15, 20, 25, 30] as const
export const CAPACITY_SWEEP_MIN_KWH = STANDARD_BATTERY_CAPACITIES_KWH[0]
export const CAPACITY_SWEEP_MAX_KWH =
  STANDARD_BATTERY_CAPACITIES_KWH[STANDARD_BATTERY_CAPACITIES_KWH.length - 1]

/** Pack price (€/kWh) for a professionally installed home battery. */
export const INSTALLED_PACK_EUR_PER_KWH = 400
/** Pack price (€/kWh) for plug-in / balcony batteries (typically cheaper per kWh). */
export const PLUGIN_PACK_EUR_PER_KWH = 300
/** Alias for installed pack price (legacy import sites). */
export const DEFAULT_PACK_EUR_PER_KWH = INSTALLED_PACK_EUR_PER_KWH
/** Professional install: fixed base covering labour, cabling, config. */
export const INSTALLED_BASE_EUR = 1000
/** Plugin / balcony batteries: no install base. */
export const PLUGIN_BASE_EUR = 0
/** Hard AC power ceiling for typical Dutch plug-in batteries. */
export const PLUGIN_MAX_INVERTER_KW = 2.5
/** Inverter standby / idle use subtracted from annual savings. */
export const STANDBY_KWH_PER_YEAR = 75
/** Simple linear capacity fade for the payback chart. */
export const ANNUAL_DEGRADATION_RATE = 0.02
/** Typical LFP warranty horizon used for payback warnings. */
export const WARRANTY_YEARS = 10
/**
 * Useful life we optimise advice for (mid of the common 10–15 year range).
 * Larger packs that only pay back after this horizon are not recommended.
 */
export const EXPECTED_BATTERY_LIFETIME_YEARS = 12
/** Hard ceiling: a battery that does not pay back by then is a poor fit. */
export const MAX_BATTERY_LIFETIME_YEARS = 15

export type BatteryType = 'plugin' | 'installed'

export function percentile(values: number[], p: number): number {
  if (values.length === 0) return 0
  const sorted = [...values].sort((a, b) => a - b)
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.ceil((p / 100) * sorted.length) - 1))
  return sorted[idx]
}

function roundHalfKw(kw: number): number {
  return Math.max(0.5, Math.ceil(kw * 2) / 2)
}

/**
 * Peak-based inverter hint: the 95th percentile of absolute hourly net grid
 * power, rounded up to the next 0.5 kW.
 */
export function recommendInverterKw(hourlyNetPowerW: number[]): number {
  const p95W = percentile(hourlyNetPowerW, INVERTER_SIZING_PERCENTILE)
  return roundHalfKw(p95W / 1000)
}

/**
 * Size the inverter for a given battery capacity and type.
 * - Plugin: min(peak, 0.5C, 2.5 kW)
 * - Installed: clamp peak into [0.5C, 1C]
 */
export function sizeInverterKw(
  capacityKwh: number,
  peakKw: number,
  type: BatteryType
): number {
  const c05 = 0.5 * capacityKwh
  const c10 = 1.0 * capacityKwh
  if (type === 'plugin') {
    return roundHalfKw(Math.min(peakKw, c05, PLUGIN_MAX_INVERTER_KW))
  }
  // Clamp peak into [0.5C, 1C].
  return roundHalfKw(Math.min(c10, Math.max(c05, peakKw)))
}

export function installBaseEur(type: BatteryType): number {
  return type === 'plugin' ? PLUGIN_BASE_EUR : INSTALLED_BASE_EUR
}

/** Default pack €/kWh for the battery type (plugin is cheaper). */
export function defaultPackEurPerKwh(type: BatteryType): number {
  return type === 'plugin' ? PLUGIN_PACK_EUR_PER_KWH : INSTALLED_PACK_EUR_PER_KWH
}

export function batteryInvestmentEur(
  capacityKwh: number,
  packEurPerKwh: number,
  type: BatteryType
): number {
  return installBaseEur(type) + capacityKwh * packEurPerKwh
}

/**
 * Average consumption price used to value standby kWh. Standby is drawn from
 * the grid, so it costs the all-in import price — averaging bare day-ahead
 * prices used to bill it at roughly a third of what it really costs.
 */
export function averageConsumptionPrice(
  priceMode: 'fixed' | 'spot',
  fixedConsumption: number,
  hourlyPricesByTime?: Map<string, number>
): number {
  if (priceMode === 'fixed') return fixedConsumption
  if (!hourlyPricesByTime || hourlyPricesByTime.size === 0) return fixedConsumption
  return spotTariff(hourlyPricesByTime).averageImportEurPerKwh
}

export function standbyCostEurPerYear(priceEurPerKwh: number): number {
  return STANDBY_KWH_PER_YEAR * priceEurPerKwh
}

/**
 * Cumulative savings after `years` years with geometric fade.
 * Year 1 contributes annualSavings; year 2 contributes annualSavings*(1-d); …
 * A part year accrues pro rata, so payback can land inside a year rather than
 * only on its boundary.
 */
export function cumulativeSavingsWithDegradation(
  annualSavingsEur: number,
  years: number,
  fadeRate = ANNUAL_DEGRADATION_RATE
): number {
  if (years <= 0 || annualSavingsEur <= 0) return 0
  const wholeYears = Math.floor(years)
  let total = 0
  for (let y = 0; y < wholeYears; y++) {
    total += annualSavingsEur * Math.pow(1 - fadeRate, y)
  }
  const partYear = years - wholeYears
  if (partYear > 0) {
    total += annualSavingsEur * Math.pow(1 - fadeRate, wholeYears) * partYear
  }
  return total
}

/** Payback years under declining annual savings (binary search). */
export function paybackYearsWithDegradation(
  costEur: number,
  annualSavingsEur: number,
  fadeRate = ANNUAL_DEGRADATION_RATE,
  maxYears = 40
): number {
  if (annualSavingsEur <= 0 || costEur <= 0) return Number.POSITIVE_INFINITY
  // Fast path: if even infinite horizon can't recover…
  const infinite =
    fadeRate > 0 && fadeRate < 1
      ? annualSavingsEur / fadeRate
      : annualSavingsEur * maxYears
  if (infinite < costEur) return Number.POSITIVE_INFINITY
  // …and if it does not recover inside the search horizon either, say so rather
  // than saturating at maxYears, which would read as a real payback.
  if (cumulativeSavingsWithDegradation(annualSavingsEur, maxYears, fadeRate) < costEur) {
    return Number.POSITIVE_INFINITY
  }

  let lo = 0
  let hi = maxYears
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2
    if (cumulativeSavingsWithDegradation(annualSavingsEur, mid, fadeRate) >= costEur) hi = mid
    else lo = mid
  }
  return hi
}

export interface CapacitySweepRow {
  capacityKwh: number
  /** Inverter used for this capacity (after C-rate / type rules). */
  inverterKw: number
  savingsEur: number
  annualSavingsEur: number
  /** Annual savings before the battery's standby use is taken off. */
  annualGrossEur: number
  /** What the battery's standby use costs a year, at the average import price. */
  standbyEur: number
  costEur: number
  packCostEur: number
  installBaseEur: number
  paybackYears: number
  roiPercent: number
  selfConsumptionPercent: number | null
  fullEquivalentCyclesPerYear: number
}

export interface SweepParams extends TariffSource {
  hourlySlots: HourlySlot[]
  periodDays: number
  /** Peak-based inverter hint (95th percentile), before C-rate clamps. */
  peakInverterKw: number
  /** Expert override: when set, every capacity uses this inverter kW. */
  inverterOverrideKw?: number | null
  batteryType: BatteryType
  strategy: BatteryStrategy
  priceMode: 'fixed' | 'spot'
  batteryCostPerKwh: number
  minSocPercent: number
  maxSocPercent?: number
  initialSocPercent: number
  tradingSharePercent?: number
}

export interface SweepResult {
  rows: CapacitySweepRow[]
}

/** Net € over a lifetime horizon: cumulative faded savings minus investment. */
export function lifetimeNetValueEur(
  annualSavingsEur: number,
  costEur: number,
  years = EXPECTED_BATTERY_LIFETIME_YEARS
): number {
  return cumulativeSavingsWithDegradation(annualSavingsEur, years) - costEur
}

/**
 * Payback slack when comparing “similar” ROI: within 1 year or 15% of the
 * fastest payback (whichever is larger).
 */
const PAYBACK_NEAR_RELATIVE = 0.15
const PAYBACK_NEAR_ABS_YEARS = 1
/**
 * Trade-only must beat the best non-trading scenario by this relative margin
 * on lifetime net value before we recommend it as primary advice.
 */
export const TRADE_ONLY_ADVANTAGE_THRESHOLD = 0.1

function paybackNearSlack(bestPayback: number): number {
  if (!Number.isFinite(bestPayback)) return PAYBACK_NEAR_ABS_YEARS
  return Math.max(PAYBACK_NEAR_ABS_YEARS, bestPayback * PAYBACK_NEAR_RELATIVE)
}

/**
 * Best capacity: fastest ROI within the lifetime window, and among near-best
 * paybacks the size that still drives the most self-consumption.
 */
export function recommendCapacity(rows: CapacitySweepRow[]): CapacitySweepRow | null {
  if (rows.length === 0) return null

  const scored = rows.map((row) => ({
    row,
    net: lifetimeNetValueEur(row.annualSavingsEur, row.costEur),
    payback: row.paybackYears,
    selfPct: row.selfConsumptionPercent,
  }))

  const withinWarranty = scored.filter(
    (c) => Number.isFinite(c.payback) && c.payback <= WARRANTY_YEARS
  )
  const withinMaxLife = scored.filter(
    (c) => Number.isFinite(c.payback) && c.payback <= MAX_BATTERY_LIFETIME_YEARS
  )
  const pool =
    withinWarranty.length > 0
      ? withinWarranty
      : withinMaxLife.length > 0
        ? withinMaxLife
        : scored

  const withPayback = pool.filter((c) => Number.isFinite(c.payback))
  const candidates = withPayback.length > 0 ? withPayback : pool

  const bestPayback = Math.min(
    ...candidates.map((c) => (Number.isFinite(c.payback) ? c.payback : Number.POSITIVE_INFINITY))
  )
  const slack = paybackNearSlack(bestPayback)
  const nearBest = candidates.filter(
    (c) => !Number.isFinite(c.payback) || c.payback <= bestPayback + slack
  )
  const finalists = nearBest.length > 0 ? nearBest : candidates

  /**
   * Self-consumption may only break a tie between sizes that earn themselves
   * back. When none of them do, the customer loses money whichever size they
   * buy, and the least-bad option is the cheapest one that comes closest — never
   * the roomiest just because it stores more of its own solar.
   */
  const paysBackInLife = finalists.some(
    (c) => Number.isFinite(c.payback) && c.payback <= MAX_BATTERY_LIFETIME_YEARS
  )

  finalists.sort((a, b) => {
    if (paysBackInLife) {
      // Prefer higher self-consumption when ROI is comparable.
      const aSc = a.selfPct ?? -1
      const bSc = b.selfPct ?? -1
      if (bSc !== aSc) return bSc - aSc
    }
    // Then fastest payback (ROI).
    if (a.payback !== b.payback) return a.payback - b.payback
    // Then higher lifetime net / annual savings.
    if (b.net !== a.net) return b.net - a.net
    return a.row.capacityKwh - b.row.capacityKwh
  })
  return finalists[0]?.row ?? null
}

export type EnergyContract = 'dynamic' | 'fixed' | 'unknown'

/**
 * The price mode a contract can honestly be advised on. Hourly (spot) pricing
 * requires a dynamic contract; a fixed contract — or one the user cannot
 * identify — may only be evaluated on its fixed tariffs.
 */
export function priceModeForContract(contract: EnergyContract): 'fixed' | 'spot' {
  return contract === 'dynamic' ? 'spot' : 'fixed'
}

/**
 * A battery earns money in exactly two ways: storing own solar instead of
 * feeding it in, and charging cheap / discharging expensive on hourly prices.
 * Without solar and without a dynamic contract neither mechanism exists, so
 * there is nothing to compute and no financial case to present.
 */
export function hasFinancialCase(contract: EnergyContract, hasSolar: boolean): boolean {
  return hasSolar || priceModeForContract(contract) === 'spot'
}

export interface ScenarioSweepInput {
  id: string
  /** Pure trading strategy — only preferred when clearly better. */
  isTradeOnly: boolean
  strategy: BatteryStrategy
  priceMode: 'fixed' | 'spot'
  tradingSharePercent: number
}

export interface ScenarioAdviceResult {
  scenarioId: string
  isTradeOnly: boolean
  row: CapacitySweepRow
  lifetimeNet: number
  paybackYears: number
  selfConsumptionPercent: number | null
}

export interface FullAdviceResult extends ScenarioAdviceResult {
  batteryType: BatteryType
  batteryCostPerKwh: number
}

export interface RecommendScenarioParams {
  scenarios: ScenarioSweepInput[]
  /** Shared sweep knobs (slots, costs, SoC, …) without strategy/priceMode/type. */
  base: Omit<
    SweepParams,
    | 'strategy'
    | 'priceMode'
    | 'tradingSharePercent'
    | 'hourlyPricesByTime'
    | 'fixedConsumptionPriceEurPerKwh'
    | 'fixedReturnPriceEurPerKwh'
    | 'batteryType'
    | 'batteryCostPerKwh'
  >
  batteryType: BatteryType
  batteryCostPerKwh: number
  hourlyPricesByTime?: Map<string, number>
  fixedConsumptionPriceEurPerKwh?: number
  fixedReturnPriceEurPerKwh?: number
  /** Feed-in costs per exported kWh; storing energy avoids them. */
  feedInFeeEurPerKwh?: number
  /** Supplier markup per kWh excl. VAT; paid on import, deducted on export. */
  supplierMarkupEurPerKwh?: number
  /** Relative edge trade-only needs over the best alternative (default 10%). */
  tradeOnlyAdvantageThreshold?: number
  /**
   * Corrects a sweep's rows before the pick, given the battery type and scenario
   * they belong to: the report moves a short history's savings where its
   * calibrated range says the estimate is off for nearly everyone (rangedRow).
   */
  adjustRows?: AdjustRows
}

export type AdjustRows = (
  rows: CapacitySweepRow[],
  option: { batteryType: BatteryType; scenarioId: string }
) => CapacitySweepRow[]

/**
 * Evaluate each usage scenario, pick capacity per scenario, then choose the
 * best scenario. Trade-only wins only when it beats the next-best by a clear margin.
 */
export function recommendScenarioAdvice(
  params: RecommendScenarioParams
): ScenarioAdviceResult | null {
  const {
    scenarios,
    base,
    batteryType,
    batteryCostPerKwh,
    hourlyPricesByTime,
    fixedConsumptionPriceEurPerKwh,
    fixedReturnPriceEurPerKwh,
    feedInFeeEurPerKwh,
    supplierMarkupEurPerKwh,
    tradeOnlyAdvantageThreshold = TRADE_ONLY_ADVANTAGE_THRESHOLD,
    adjustRows,
  } = params

  const results: ScenarioAdviceResult[] = []
  for (const scenario of scenarios) {
    if (scenario.priceMode === 'spot' && (!hourlyPricesByTime || hourlyPricesByTime.size === 0)) {
      continue
    }
    const sweep = sweepCapacities({
      ...base,
      batteryType,
      batteryCostPerKwh,
      strategy: scenario.strategy,
      priceMode: scenario.priceMode,
      tradingSharePercent: scenario.tradingSharePercent,
      hourlyPricesByTime: scenario.priceMode === 'spot' ? hourlyPricesByTime : undefined,
      fixedConsumptionPriceEurPerKwh:
        scenario.priceMode === 'fixed' ? fixedConsumptionPriceEurPerKwh : undefined,
      fixedReturnPriceEurPerKwh:
        scenario.priceMode === 'fixed' ? fixedReturnPriceEurPerKwh : undefined,
      feedInFeeEurPerKwh,
      supplierMarkupEurPerKwh,
    })
    const rows = adjustRows ? adjustRows(sweep.rows, { batteryType, scenarioId: scenario.id }) : sweep.rows
    const row = recommendCapacity(rows)
    if (!row) continue
    results.push({
      scenarioId: scenario.id,
      isTradeOnly: scenario.isTradeOnly,
      row,
      lifetimeNet: lifetimeNetValueEur(row.annualSavingsEur, row.costEur),
      paybackYears: row.paybackYears,
      selfConsumptionPercent: row.selfConsumptionPercent,
    })
  }

  if (results.length === 0) return null

  const byNet = [...results].sort((a, b) => {
    if (b.lifetimeNet !== a.lifetimeNet) return b.lifetimeNet - a.lifetimeNet
    if (a.paybackYears !== b.paybackYears) return a.paybackYears - b.paybackYears
    const aSc = a.selfConsumptionPercent ?? -1
    const bSc = b.selfConsumptionPercent ?? -1
    return bSc - aSc
  })

  const best = byNet[0]!
  if (!best.isTradeOnly) return best

  const bestAlternative = byNet.find((r) => !r.isTradeOnly)
  if (!bestAlternative) return best

  const denom = Math.max(Math.abs(bestAlternative.lifetimeNet), 1)
  const edge = (best.lifetimeNet - bestAlternative.lifetimeNet) / denom
  // Trade-only only if clearly better; otherwise keep the stronger self-use option.
  if (edge < tradeOnlyAdvantageThreshold) return bestAlternative
  return best
}

export interface RecommendFullAdviceParams {
  scenarios: ScenarioSweepInput[]
  /** Battery types to compare (default installed + plugin). */
  batteryTypes?: BatteryType[]
  base: RecommendScenarioParams['base']
  hourlyPricesByTime?: Map<string, number>
  fixedConsumptionPriceEurPerKwh?: number
  fixedReturnPriceEurPerKwh?: number
  feedInFeeEurPerKwh?: number
  supplierMarkupEurPerKwh?: number
  tradeOnlyAdvantageThreshold?: number
  /** See RecommendScenarioParams.adjustRows. */
  adjustRows?: AdjustRows
}

/**
 * Pick the best combination of battery type (plugin vs installed) and usage
 * scenario. Uses each type’s default pack €/kWh. Plugin wins when its lifetime
 * net is strictly better than installed.
 */
export function recommendFullAdvice(params: RecommendFullAdviceParams): FullAdviceResult | null {
  const types = params.batteryTypes ?? (['installed', 'plugin'] as BatteryType[])
  const perType: FullAdviceResult[] = []

  for (const batteryType of types) {
    const pack = defaultPackEurPerKwh(batteryType)
    const scenario = recommendScenarioAdvice({
      scenarios: params.scenarios,
      base: params.base,
      batteryType,
      batteryCostPerKwh: pack,
      hourlyPricesByTime: params.hourlyPricesByTime,
      fixedConsumptionPriceEurPerKwh: params.fixedConsumptionPriceEurPerKwh,
      fixedReturnPriceEurPerKwh: params.fixedReturnPriceEurPerKwh,
      feedInFeeEurPerKwh: params.feedInFeeEurPerKwh,
      supplierMarkupEurPerKwh: params.supplierMarkupEurPerKwh,
      tradeOnlyAdvantageThreshold: params.tradeOnlyAdvantageThreshold,
      adjustRows: params.adjustRows,
    })
    if (!scenario) continue
    perType.push({
      ...scenario,
      batteryType,
      batteryCostPerKwh: pack,
    })
  }

  if (perType.length === 0) return null

  perType.sort((a, b) => {
    if (b.lifetimeNet !== a.lifetimeNet) return b.lifetimeNet - a.lifetimeNet
    if (a.paybackYears !== b.paybackYears) return a.paybackYears - b.paybackYears
    const aSc = a.selfConsumptionPercent ?? -1
    const bSc = b.selfConsumptionPercent ?? -1
    if (bSc !== aSc) return bSc - aSc
    // Prefer installed when economics are equal (professional default).
    if (a.batteryType !== b.batteryType) {
      return a.batteryType === 'installed' ? -1 : 1
    }
    return 0
  })
  return perType[0] ?? null
}

/**
 * Simulate each standard battery size with a type-aware inverter and
 * annualize savings (minus standby), investment, payback (with fade) and ROI.
 */
export function sweepCapacities(params: SweepParams): SweepResult {
  const {
    hourlySlots,
    periodDays,
    peakInverterKw,
    inverterOverrideKw,
    batteryType,
    strategy,
    fixedConsumptionPriceEurPerKwh,
    batteryCostPerKwh,
    minSocPercent,
    maxSocPercent,
    initialSocPercent,
    tradingSharePercent,
  } = params

  // One tariff for the whole sweep: every capacity is judged on the same prices.
  const tariff = resolveTariff(params)
  const standby = standbyCostEurPerYear(
    tariff?.averageImportEurPerKwh ?? fixedConsumptionPriceEurPerKwh ?? 0.26
  )

  const rows: CapacitySweepRow[] = []
  for (const cap of STANDARD_BATTERY_CAPACITIES_KWH) {
    const inverterKw =
      inverterOverrideKw != null && inverterOverrideKw > 0
        ? inverterOverrideKw
        : sizeInverterKw(cap, peakInverterKw, batteryType)

    const sim = runBatterySimulation({
      hourlySlots,
      tariff,
      capacityKwh: cap,
      inverterKw,
      minSocPercent,
      maxSocPercent,
      initialSocKwh: cap * (initialSocPercent / 100),
      strategy,
      tradingSharePercent,
    })
    const periodSavings = Math.max(0, sim.savingsEur ?? 0)
    const annualGross = periodDays > 0 ? (periodSavings * 365) / periodDays : 0
    const annualSavingsEur = Math.max(0, annualGross - standby)
    const packCostEur = cap * batteryCostPerKwh
    const base = installBaseEur(batteryType)
    const costEur = packCostEur + base
    const paybackYears = paybackYearsWithDegradation(costEur, annualSavingsEur)
    const roiPercent = costEur > 0 ? (annualSavingsEur / costEur) * 100 : 0
    const cyclesPerYear =
      periodDays > 0 ? (sim.fullEquivalentCycles * 365) / periodDays : 0

    rows.push({
      capacityKwh: cap,
      inverterKw,
      savingsEur: periodSavings,
      annualSavingsEur,
      annualGrossEur: annualGross,
      standbyEur: standby,
      costEur,
      packCostEur,
      installBaseEur: base,
      paybackYears,
      roiPercent,
      selfConsumptionPercent: sim.selfConsumptionPercent,
      fullEquivalentCyclesPerYear: cyclesPerYear,
    })
  }

  return { rows }
}
